import {
    Box,
    Card,
    Close,
    Flex,
    Grid,
    Heading,
    Image,
    Spinner,
    Text
} from '@theme-ui/components'
import { useTranslation } from 'react-i18next'
import { toast } from 'utils/toast'
import { setInstitutionUsers } from "features/auth/authSlice"
import { useDispatch, useSelector } from 'react-redux'
import {
    Button,
    Circle,
    ImagePlaceholder
} from '~/Common'
import { confirmAlert } from 'react-confirm-alert'
import { ThemeProvider } from 'theme-ui'
import { theme } from 'app/theme'
import { unlinkUser } from 'features/institutions/institutionsAPI'
import { CSSTransition } from 'react-transition-group'
import { useState, useRef } from 'react'
import { Icon } from 'assets/Icon'

export const Users = ({ institutionUsers, showEmptyNotice = true }) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState(institutionUsers ?? undefined);
    const loggedInUser = useSelector(state => state.user?.user);

    const handleUnlinkClick = (e, uuid) => {
        e.preventDefault();

        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ThemeProvider theme={theme}>
                        <Box
                            bg="white"
                            px={5}
                            py={4}
                            sx={{
                                maxWidth: '600px',
                                width: '100%',
                                textAlign: 'center',
                                boxShadow: '0 20px 75px rgb(0 0 0 / 23%)',
                                position: 'relative',
                            }}
                        >
                            <Close
                                onClick={onClose}
                                sx={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                }}
                            ></Close>

                            <Heading as="h2" variant="h2" mb={3}>
                                {t('Are you sure?')}
                            </Heading>
                            <Text as="p" mb={3}>
                                {t('Are you sure to want to unlink this user?')}
                            </Text>
                            <Button
                                id="confirmUnlink"
                                variant="primary"
                                mr={3}
                                onClick={() => {
                                    confirmUnlink(uuid)
                                    onClose();
                                }}
                            >
                                {t('Yes')}
                            </Button>
                            <Button variant="link" onClick={onClose}>
                                {t('Cancel')}
                            </Button>
                        </Box>
                    </ThemeProvider>
                );
            },
        });
    };

    const confirmUnlink = (uuid) => {
        setLoading(true);
        unlinkUser(uuid).then((response) => {
            toast.success(response?.message ?? t('institutions.unlink.success'), {
                toastId: 'unlink-success',
            });

            dispatch(setInstitutionUsers(response));
            setUsers(response?.data);

        }).catch(({ response }) => {
            toast.error(response?.data?.message ?? t('institutions.unlink.error'), {
                toastId: 'unlink-error',
            });
        }).finally(() => setLoading(false))
    };

    return <>
        {loading ? <Spinner variant="styles.spinner" m={'auto'} /> : (
            <CSSTransition
                nodeRef={nodeRef}
                in={true}
                timeout={500}
                classNames="fade"
                unmountOnExit
                appear
            >
                <Box ref={nodeRef} id="currentinstitutionUsers">
                    {users !== undefined && users.length ? (
                        <>
                            {users.map((user, key) => (
                                <Card
                                    key={`institutionUser${key}`}
                                    variant="list"
                                    as={Box}
                                    mb={3}
                                >
                                    <Grid px={3} py={3} sx={{
                                        position: 'relative',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gridTemplate: ['1fr', '1fr', 'auto / 50px auto 200px 170px'],
                                        gridGap: '24px',
                                    }}>
                                        <Flex sx={{
                                            width: '80px',
                                            alignItems: 'center',
                                        }}>
                                            <Circle size="39px" bg="#f4f4f4" type="box" sx={{ flexShrink: 0, overflow: 'hidden', mr: 2 }}>
                                                {user?.profile_photo_url ? (
                                                    <Image src={user?.profile_photo_url} sx={{ borderRadius: '50%' }} />
                                                ) : (
                                                    <ImagePlaceholder imageSx={{ borderRadius: '50%' }} sx={{
                                                        width: '70px',
                                                        height: '70px',
                                                        borderRadius: '50%'
                                                    }} />
                                                )}
                                            </Circle>
                                        </Flex>

                                        <Box pb={[3,3,0]} sx={{
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: ['calc(100% - 80px)', 'auto'],
                                            maxWidth: '300px',
                                        }}>
                                            <Heading as="h3" variant="deal_heading_list" mb={0}>
                                                {t(`${user.first_name} ${user.last_name}`)}
                                            </Heading>
                                            <Text as="p" variant="tag" color="dark">{user?.email}</Text>
                                        </Box>

                                        <Box pb={[3, 3, 0]} sx={{
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            maxWidth: '300px',
                                        }}>
                                            <Flex sx={{
                                                justifyContent: "space-between"
                                            }}>
                                                <Text as="p" variant="tag" color="dark">
                                                    {t('Finished onboarding?')}
                                                </Text>
                                                <Text as="p" variant="tag" color="dark">
                                                    {t(user?.finished_onboarding_details ? 'Yes' : 'No')}
                                                </Text>
                                            </Flex>
                                            <Flex sx={{
                                                justifyContent: "space-between"
                                            }}>
                                                <Text as="p" variant="tag" color="dark">
                                                    {t('Authorised signatory?')}
                                                </Text>
                                                <Text as="p" variant="tag" color="dark">
                                                    {t(user?.is_signatory ? 'Yes' : 'No')}
                                                </Text>
                                            </Flex>
                                            <Flex sx={{
                                                justifyContent: "space-between"
                                            }}>
                                                <Text as="p" variant="tag" color="dark">
                                                    {t('Director?')}
                                                </Text>
                                                <Text as="p" variant="tag" color="dark">
                                                    {t(user?.is_director ? 'Yes' : 'No')}
                                                </Text>
                                            </Flex>
                                        </Box>

                                        {user?.uuid !== loggedInUser.uuid && (
                                            <Flex p={[null, null, 2]} sx={{
                                                width: '100%',
                                                marginLeft: 'auto',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                            }}>
                                                <Button variant="bordered" className="unlinkUser" onClick={(e) => handleUnlinkClick(e, user.uuid)}>{t(`Unlink account`)}</Button>
                                            </Flex>
                                        )}
                                    </Grid>
                                </Card>
                            ))}
                        </>
                    ) : (
                        showEmptyNotice ?
                            <Box py={[4, 5]} px={[3, 4, 6]} mb={4} sx={{
                                bg: 'light',
                                borderRadius: '16px',
                                textAlign: 'center'
                            }}>
                                <Icon sx={{ color: 'tealDark' }} icon="users" size="48px" mb={3} />
                                <Heading as="h2" variant="h2" mb={4}>{t('No other users currently linked to your institution')}</Heading>
                            </Box> : null
                    )}
                </Box>
            </CSSTransition>
        )}
    </>;
};
